import { action, computed, observable } from 'mobx';
import { isInSubnet } from 'core/util/ip';
import type { BgpPrefix, ReachabilityTimeseriesResponse, EnrichedASPathGraphSeries } from './bgpRequests';
import { fetchBgpSubPrefixes, fetchBgpReachabilityTimeseries, fetchBgpASPathGraphTimeseries } from './bgpRequests';

/** Minimal root store shape for cross-store access */
interface RootStore {
  $colors: {
    qualitativeColors: string[];
  };
}

export interface FetchSubPrefixesParams {
  prefixes: BgpPrefix[];
  includeCovered?: boolean;
  startDate?: number;
  endDate?: number;
}

export interface FetchReachabilityTimeseriesParams {
  prefixes: BgpPrefix[];
  includeCovered?: boolean;
  startDate?: number;
  endDate?: number;
  vpKeys?: string[];
  groupBy?: number[];
}

export interface FetchASPathGraphTimeseriesParams {
  prefixes: BgpPrefix[];
  includeCovered?: boolean;
  startDate?: number;
  endDate?: number;
  vpKeys?: string[];
  pruningPercentage?: number;
}

class BgpStore {
  /** Set by root store constructor — provides cross-store access */
  store!: RootStore;

  @observable
  subPrefixesLoading = false;

  @observable
  subPrefixesData: BgpPrefix[] | null = null;

  @observable
  subPrefixesError: Error | null = null;

  @observable
  reachabilityTimeseriesLoading = false;

  @observable
  reachabilityTimeseriesData: ReachabilityTimeseriesResponse | null = null;

  @observable
  reachabilityTimeseriesError: Error | null = null;

  @observable
  asPathGraphTimeseriesLoading = false;

  @observable
  asPathGraphTimeseriesData: EnrichedASPathGraphSeries | null = null;

  @observable
  asPathGraphTimeseriesError: Error | null = null;

  @observable
  asnColorMap = observable.map<string, string>();

  // Track the next palette index to use (not observable - internal bookkeeping)
  private nextColorIndex = 0;

  @action
  reset(): void {
    this.subPrefixesLoading = false;
    this.subPrefixesData = null;
    this.subPrefixesError = null;
    this.reachabilityTimeseriesLoading = false;
    this.reachabilityTimeseriesData = null;
    this.reachabilityTimeseriesError = null;
    this.asPathGraphTimeseriesLoading = false;
    this.asPathGraphTimeseriesData = null;
    this.asPathGraphTimeseriesError = null;
    this.asnColorMap.clear();
    this.nextColorIndex = 0;
  }

  @action
  setSubPrefixesLoading(loading: boolean): void {
    this.subPrefixesLoading = loading;
  }

  @action
  setSubPrefixesData(data: BgpPrefix[]): void {
    this.subPrefixesData = data.map((pfx) => pfx.trim());
    this.subPrefixesError = null;
  }

  @action
  setSubPrefixesError(error: Error): void {
    this.subPrefixesError = error;
    this.subPrefixesData = null;
  }

  @action
  setReachabilityTimeseriesLoading(loading: boolean): void {
    this.reachabilityTimeseriesLoading = loading;
  }

  @action
  setReachabilityTimeseriesData(data: ReachabilityTimeseriesResponse): void {
    this.reachabilityTimeseriesData = data;
    this.reachabilityTimeseriesError = null;

    // Register any new ASNs from the reachability response
    if (data.lookups?.asns) {
      this.registerAsns(Object.keys(data.lookups.asns));
    }
  }

  @action
  setReachabilityTimeseriesError(error: Error): void {
    this.reachabilityTimeseriesError = error;
    this.reachabilityTimeseriesData = null;
  }

  @action
  setASPathGraphTimeseriesLoading(loading: boolean): void {
    this.asPathGraphTimeseriesLoading = loading;
  }

  @action
  setASPathGraphTimeseriesData(data: EnrichedASPathGraphSeries): void {
    this.asPathGraphTimeseriesData = data;
    this.asPathGraphTimeseriesError = null;

    // Register any new ASNs from the AS Path Graph response
    if (data.lookups?.asns) {
      this.registerAsns(Object.keys(data.lookups.asns));
    }
  }

  @action
  setASPathGraphTimeseriesError(error: Error): void {
    this.asPathGraphTimeseriesError = error;
    this.asPathGraphTimeseriesData = null;
  }

  @computed
  get hasSubPrefixes(): boolean {
    return Boolean(this.subPrefixesData && this.subPrefixesData.length > 0);
  }

  @computed
  get subPrefixesByPrefix() {
    return (prefix: string): BgpPrefix[] => {
      if (!this.subPrefixesData || this.subPrefixesData.length === 0) {
        return [];
      }

      return this.subPrefixesData.filter((subPfx) => isInSubnet(subPfx, prefix));
    };
  }

  @computed
  get isLoading(): boolean {
    return this.subPrefixesLoading || this.reachabilityTimeseriesLoading || this.asPathGraphTimeseriesLoading;
  }

  /**
   * Register ASN IDs in the shared color map.
   * New ASNs get the next available chart color (round-robin).
   * Already-registered ASNs are skipped (stable mapping).
   */
  @action
  private registerAsns(asnIds: string[]): void {
    const { qualitativeColors } = this.store.$colors;
    if (!qualitativeColors || qualitativeColors.length === 0) {
      return;
    }
    const colors = [...qualitativeColors];

    for (const id of asnIds) {
      if (!this.asnColorMap.has(id)) {
        this.asnColorMap.set(id, colors[this.nextColorIndex % colors.length]);
        this.nextColorIndex += 1;
      }
    }
  }

  /**
   * Fetch sub-prefixes for the given monitored prefixes.
   * Sets subPrefixesData/subPrefixesError and manages loading state.
   */
  @action
  async fetchSubPrefixes({
    prefixes,
    includeCovered,
    startDate,
    endDate
  }: FetchSubPrefixesParams): Promise<BgpPrefix[]> {
    this.setSubPrefixesLoading(true);

    try {
      const data = await fetchBgpSubPrefixes({
        prefixes,
        include_covered: includeCovered,
        start_date: startDate,
        end_date: endDate
      });

      this.setSubPrefixesData(data);
      return data;
    } catch (error) {
      this.setSubPrefixesError(error as Error);
      throw error;
    } finally {
      this.setSubPrefixesLoading(false);
    }
  }

  /**
   * Fetch reachability timeseries for the given prefixes.
   * Sets reachabilityTimeseriesData/Error, registers new ASNs in the color map.
   */
  @action
  async fetchReachabilityTimeseries({
    prefixes,
    includeCovered,
    startDate,
    endDate,
    vpKeys
  }: FetchReachabilityTimeseriesParams): Promise<ReachabilityTimeseriesResponse> {
    this.setReachabilityTimeseriesLoading(true);

    try {
      const data = await fetchBgpReachabilityTimeseries({
        prefixes,
        include_covered: includeCovered,
        start_date: startDate,
        end_date: endDate,
        vp_keys: vpKeys
      });

      this.setReachabilityTimeseriesData(data);
      return data;
    } catch (error) {
      this.setReachabilityTimeseriesError(error as Error);
      throw error;
    } finally {
      this.setReachabilityTimeseriesLoading(false);
    }
  }

  /**
   * Fetch AS Path Graph timeseries for the given prefixes.
   * Sets asPathGraphTimeseriesData/Error, registers new ASNs in the color map.
   */
  @action
  async fetchASPathGraphTimeseries({
    prefixes,
    includeCovered,
    startDate,
    endDate,
    vpKeys,
    pruningPercentage
  }: FetchASPathGraphTimeseriesParams): Promise<EnrichedASPathGraphSeries> {
    this.setASPathGraphTimeseriesLoading(true);

    try {
      const data = await fetchBgpASPathGraphTimeseries({
        prefixes,
        include_covered: includeCovered,
        start_date: startDate,
        end_date: endDate,
        vp_keys: vpKeys,
        pruning_percentage: pruningPercentage
      });

      this.setASPathGraphTimeseriesData(data);
      return data;
    } catch (error) {
      this.setASPathGraphTimeseriesError(error as Error);
      throw error;
    } finally {
      this.setASPathGraphTimeseriesLoading(false);
    }
  }
}

export default new BgpStore();
